import { Injectable } from "@angular/core";
import { GoogleTagManagerService } from "angular-google-tag-manager";
import { UsersService } from "./users.service";
import { EnvironmentService } from "./environment.service";

declare global {
  interface Window {
    gtag?: (command: "event", eventName: string, params: Ga4CollectionPayload) => void;
    dataLayer?: unknown[];
  }
}

enum CustomEvents {
  NewClientOpen = "new_client_open",
  NewClientAdded = "new_client_added",
  BCResponseTime = "bc_response_time",
  BCResponseToEmail = "bc_response_email",
  BCResponseFailure = "bc_response_failure",
  QuoteRequestCreated = "quote_request_created",
  QuoteRequestUpdated = "quote_request_updated",
  QuoteDownload = "quote_download",
}

/** GA4 collection module event names. Sent via pushGa4Event */
enum CollectionGa4Events {
  DashboardVisit = "collection_dashboard_visit",
  NoteRegistered = "collection_note_registered",
  AddressRegistered = "collection_address_registered",
  ContactRegistered = "collection_contact_registered",
  StpAccountSent = "collection_stp_account_sent",
  OccupationRegistered = "collection_occupation_registered",
  ExportDownloaded = "collection_export_downloaded",
  CustomerProfileVisit = "collection_customer_profile_visit",
}

/** GA4 collection event payload. */
interface Ga4CollectionPayload {
  event: CollectionGa4Events;
  collector_user_id: string;
  user_full_name: string;
  user_tipo: string;
  bucket_id?: string;
  phase_id?: string;
  collector_account_id?: string;
  contact_type?: "phone" | "email";
}

interface Ga4CollectionEventParams {
  collector_account_id?: string;
  contact_type?: "phone" | "email";
}

interface CustomEventParams {
  [key: string]: any;
}

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private current_user: any;
  private isGa4Loaded = false;
  private collectionContext: { bucket_id?: string; phase_id?: string } = {};

  constructor(
    private _usersService: UsersService,
    private gtmService: GoogleTagManagerService,
    private _envService: EnvironmentService
  ) {
    this.current_user = this._usersService.getCurrentUser();
    this.initializeGa4WhenReady();
  }

  private initializeGa4WhenReady(): void {
    if (!this._envService.isConfigLoaded()) {
      setTimeout(() => this.initializeGa4WhenReady(), 100);
      return;
    }
    this.initGa4IfEnabled();
  }

  initGa4IfEnabled(): void {
    const ga4 = this._envService.get("ga4_collection_analytics");
    if (!ga4?.enabled || !ga4.measurement_id) {
      return;
    }
    this.loadGa4(ga4.measurement_id);
  }

  setCollectionContext(ctx: { bucket_id?: string; phase_id?: string }): void {
    this.collectionContext = { ...ctx };
  }

  private loadGa4(measurementId: string): void {
    if (this.isGa4Loaded) return;

    const script1 = document.createElement("script");
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    const script2 = document.createElement("script");
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}');
    `;
    document.head.appendChild(script2);

    this.isGa4Loaded = true;
  }

  private pushGa4Event(eventName: CollectionGa4Events, params?: Ga4CollectionEventParams): void {
    const payload = this.buildGa4CollectionPayload(eventName, params);
    if (payload) {
      this.sendToGa4(eventName, payload);
    }
  }

  /**
   * Build GA4 collection payload when GA4 is enabled
   */
  private buildGa4CollectionPayload(
    eventName: CollectionGa4Events,
    params?: Ga4CollectionEventParams
  ): Ga4CollectionPayload | null {
    const ga4 = this._envService.get("ga4_collection_analytics");
    if (!ga4?.enabled || !this.isGa4Loaded) return null;
    if (!this.current_user) return null;
    const { id, full_name, tipo } = this.current_user;
    const payload: Ga4CollectionPayload = {
      event: eventName,
      collector_user_id: String(id),
      user_full_name: full_name,
      user_tipo: tipo,
      ...this.collectionContext,
      ...params,
    };
    return payload;
  }

  /**
   * Send payload to GA4 via gtag or dataLayer.
   */
  private sendToGa4(eventName: CollectionGa4Events, payload: Ga4CollectionPayload): void {
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(["event", eventName, payload]);
    }
  }

  private trackEvent(eventName: CustomEvents, params?: CustomEventParams): void {
    const { id, full_name, tipo } = this.current_user;
    const event = {
      event: eventName,
      user_id: id,
      user_full_name: full_name,
      user_tipo: tipo,
      ...params,
    };

    this.gtmService.pushTag(event);
  }

  /**
   * Track when user visits the collection dashboard. Sends to GA4 when enabled.
   */
  trackCollectionDashboardVisit(): void {
    this.pushGa4Event(CollectionGa4Events.DashboardVisit);
  }

  /**
   * Track when user registers a note/action in collection. Sends to GA4 when enabled.
   */
  trackCollectionNoteRegistered(params: { collector_account_id: string | null }): void {
    this.pushGa4Event(CollectionGa4Events.NoteRegistered, {
      collector_account_id: params.collector_account_id ?? "",
    });
  }

  /**
   * Track when user registers a new address in collection. Sends to GA4 when enabled.
   */
  trackCollectionAddressRegistered(params: { collector_account_id: string | null }): void {
    this.pushGa4Event(CollectionGa4Events.AddressRegistered, {
      collector_account_id: params.collector_account_id ?? "",
    });
  }

  /**
   * Track when user registers a new phone or email in collection. Sends to GA4 when enabled.
   */
  trackCollectionContactRegistered(params: {
    contact_type: "phone" | "email";
    collector_account_id: string | null;
  }): void {
    this.pushGa4Event(CollectionGa4Events.ContactRegistered, {
      contact_type: params.contact_type,
      collector_account_id: params.collector_account_id ?? "",
    });
  }

  /**
   * Track when user sends STP account to client in collection. Sends to GA4 when enabled.
   */
  trackCollectionStpAccountSent(params: { collector_account_id: string }): void {
    this.pushGa4Event(CollectionGa4Events.StpAccountSent, {
      collector_account_id: params.collector_account_id,
    });
  }

  /**
   * Track when user registers a new occupation in collection. Sends to GA4 when enabled.
   */
  trackCollectionOccupationRegistered(params: { collector_account_id: string }): void {
    this.pushGa4Event(CollectionGa4Events.OccupationRegistered, {
      collector_account_id: params.collector_account_id,
    });
  }

  /**
   * Track when user downloads portfolio export in collection. Sends to GA4 when enabled.
   */
  trackCollectionExportDownloaded(): void {
    this.pushGa4Event(CollectionGa4Events.ExportDownloaded);
  }

  /**
   * Track when user visits a customer profile in collection. Sends to GA4 when enabled.
   */
  trackCollectionCustomerProfileVisit(): void {
    this.pushGa4Event(CollectionGa4Events.CustomerProfileVisit);
  }

  /**
   * Event to track when a new customer is created.
   */
  newClientOpenEvent(): void {
    this.trackEvent(CustomEvents.NewClientOpen);
  }

  /**
   * Event to track when entering to add a new customer.
   */
  newClientAddedEvent(contact_id: number): void {
    this.trackEvent(CustomEvents.NewClientAdded);
  }

  /**
   * Event to track the response time for each request to BC.
   */
  bcResponseTimeEvent(bureau_id: number, startTime: Date): void {
    const endTime = new Date();
    const diff = (endTime.getTime() - startTime.getTime()) / 1000;
    const params = {
      bureau_id,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      diff_seconds: diff,
    };

    this.trackEvent(CustomEvents.BCResponseTime, params);
  }

  /**
   * Event to track when the BC request is submitted for email delivery.
   */
  bcResponseToEmailEvent(bureau_id: number): void {
    this.trackEvent(CustomEvents.BCResponseToEmail, { bureau_id });
  }

  /**
   * Event to track whenever a request to BC fails.
   */
  bcResponseFailureEvent(bureau_id: number, error: string, startTime: Date): void {
    const endTime = new Date();
    const diff = (endTime.getTime() - startTime.getTime()) / 1000;
    const params = {
      bureau_id,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      diff_seconds: diff,
      error,
    };

    this.trackEvent(CustomEvents.BCResponseFailure, params);
  }

  /**
   * Event to track whenever a user creates a quote.
   */
  quoteRequestUpdatedEvent(quote_id: number, quote_product: string): void {
    this.trackEvent(CustomEvents.QuoteRequestUpdated, { quote_id, quote_product });
  }

  /**
   * Event to track whenever a user updates a quote.
   */
  quoteRequestCreatedEvent(person_id: number, quote_product: string): void {
    this.trackEvent(CustomEvents.QuoteRequestCreated, { person_id, quote_product });
  }

  /**
   * Event to track whenever a user downloads a quote.
   */
  quoteDownloadEvent(quote_id: number): void {
    this.trackEvent(CustomEvents.QuoteDownload, { quote_id });
  }
}
